// 🔍 SERVIÇO DE BUSCA DE CONTEXTO
// Sistema: HEPATO TX RONDÔNIA

import { SearchResult, RetrievedContext, GuidelineCategory } from "../types";
import { DocumentoRagRecuperado } from "../../types";
import { penalidadeVigencia } from "../corpusGovernanca";
import { ragConfig } from "../config";
import { enrichQuery } from "./embeddings";
import { reciprocalRankFusion } from "./fusaoRRF";
import {
  buscarChunksDensos,
  buscarChunksHibridos,
  rerankChunksRemoto,
} from "./ragClient";

export async function buscarContextoGuidelines(
  query: string,
  options?: {
    categories?: GuidelineCategory[];
    organizations?: string[];
    topK?: number;
    minScore?: number;
  }
): Promise<RetrievedContext> {
  const startTime = Date.now();

  try {
    const enrichedQuery = enrichQuery(query);
    console.log(
      `🔍 Query enriquecida: "${enrichedQuery.substring(0, 100)}..."`
    );

    const topK = options?.topK || ragConfig.search.topK;

    // 🌐 B95 (AI_64): dense via backend autenticado (era Voyage embed + Pinecone direto do client)
    let results = await buscarChunksDensos([enrichedQuery], {
      categories: options?.categories,
      organizations: options?.organizations,
      topK,
    });

    const minScore = options?.minScore || ragConfig.search.minScore;
    results = results.filter((r) => r.score >= minScore);

    results = sortByRelevance(results);

    const searchTimeMs = Date.now() - startTime;

    console.log(
      `✅ Busca concluída: ${results.length} resultados em ${searchTimeMs}ms`
    );

    return {
      chunks: results,
      query: enrichedQuery,
      totalResults: results.length,
      searchTimeMs,
    };
  } catch (error) {
    console.error("❌ Erro na busca de contexto:", error);

    return {
      chunks: [],
      query,
      totalResults: 0,
      searchTimeMs: Date.now() - startTime,
    };
  }
}

/**
 * B358 (AI_156) — TETO MACIO POR DOCUMENTO.
 * Percorre os trechos em ordem de relevância aceitando no máximo `porDocumento`
 * de cada documento; se faltarem documentos para preencher `maxTotal`, completa
 * com os próximos mais relevantes (mesmo repetindo documento). Assim um único
 * documento não monopoliza o contexto, mas um caso com um só documento útil
 * (ex.: doador vivo) continua recebendo as vagas inteiras.
 */
export function limitarPorDocumento(
  results: SearchResult[],
  maxTotal: number,
  porDocumento: number = 2
): SearchResult[] {
  const escolhidos: SearchResult[] = [];
  const sobra: SearchResult[] = [];
  const contagem = new Map<string, number>();
  for (const r of results) {
    const chave = r.chunk.metadata.source || r.chunk.metadata.title || r.chunk.id;
    const n = contagem.get(chave) || 0;
    if (n < porDocumento && escolhidos.length < maxTotal) {
      escolhidos.push(r);
      contagem.set(chave, n + 1);
    } else {
      sobra.push(r);
    }
  }
  for (const r of sobra) {
    if (escolhidos.length >= maxTotal) break;
    escolhidos.push(r);
  }
  return escolhidos;
}

/**
 * B358 (AI_156) — DISTRIBUIÇÃO DAS VAGAS POR TEMA.
 * Recebe uma lista reranqueada por tema (uma por query) e escolhe em RODÍZIO:
 * o melhor trecho ainda não escolhido do tema 1, depois do tema 2, e assim por
 * diante, respeitando o teto de `porDocumento` trechos por documento. Se as
 * vagas não fecharem, completa sem o teto, intercalando as listas por posição.
 */
export function selecionarPorTema(
  listas: SearchResult[][],
  maxTotal: number,
  porDocumento: number = 2
): SearchResult[] {
  const escolhidos: SearchResult[] = [];
  const ids = new Set<string>();
  const contagem = new Map<string, number>();
  const chave = (r: SearchResult) =>
    r.chunk.metadata.source || r.chunk.metadata.title || r.chunk.id;
  const pos = listas.map(() => 0);

  let avancou = true;
  while (escolhidos.length < maxTotal && avancou) {
    avancou = false;
    for (let i = 0; i < listas.length && escolhidos.length < maxTotal; i++) {
      const lista = listas[i];
      while (pos[i] < lista.length) {
        const r = lista[pos[i]++];
        if (ids.has(r.chunk.id)) continue;
        const n = contagem.get(chave(r)) || 0;
        if (n >= porDocumento) continue;
        escolhidos.push(r);
        ids.add(r.chunk.id);
        contagem.set(chave(r), n + 1);
        avancou = true;
        break;
      }
    }
  }

  const maior = Math.max(0, ...listas.map((l) => l.length));
  for (let j = 0; j < maior && escolhidos.length < maxTotal; j++) {
    for (const lista of listas) {
      const r = lista[j];
      if (!r || ids.has(r.chunk.id)) continue;
      escolhidos.push(r);
      ids.add(r.chunk.id);
      if (escolhidos.length >= maxTotal) break;
    }
  }
  return escolhidos;
}

export function sortByRelevance(results: SearchResult[]): SearchResult[] {
  // Boost por autoridade da sociedade emissora (desempate quando scores ~iguais).
  // [B230, AI_103] Ampliado após auditoria do corpus (leitura dos PDFs): a detecção
  // de organização foi corrigida no Pinecone (44→16 docs em 'Other'); estas novas
  // sociedades passam a existir no índice e merecem prioridade.
  // NCCN propositalmente AUSENTE: pendência legal B234 (EULA NCCN proíbe uso com IA).
  // [B231, AI_108] ESMO REMOVIDO do boost: a curadoria de escopo (decisão de Leonardo)
  // despriorizou o cluster pâncreas, e o ÚNICO doc ESMO do corpus é a guideline de
  // pâncreas metastático (fora do núcleo hepatologia/Tx). Se um dia entrar uma guideline
  // ESMO DENTRO do escopo (ex.: CHC/oncologia hepática), reintroduzir ESMO aqui.
  const orgPriority: Record<string, number> = {
    ILTS: 10,
    "MS-Brasil": 10, // Portaria 8.041 — norma vinculante de Tx no Brasil
    "SES-SP": 10, // B358-FU (AI_156): NT 01/2011 + 02/2013 — norma VIGENTE do fígado em SP
    AASLD: 9,
    EASL: 9,
    Baveno: 9, // Baveno VII — hipertensão portal (consórcio oficial EASL)
    AST: 8,
    Tokyo: 8, // Tokyo Guidelines (TG18) — colangite/colecistite aguda
    IDSA: 7,
    JSHBPS: 7, // Japanese Soc. of Hepato-Biliary-Pancreatic Surgery
  };

  return results.sort((a, b) => {
    const scoreDiff = b.score - a.score;
    if (Math.abs(scoreDiff) > 0.05) {
      return scoreDiff;
    }

    const orgA = orgPriority[a.chunk.metadata.organization || ""] || 0;
    const orgB = orgPriority[b.chunk.metadata.organization || ""] || 0;
    if (orgA !== orgB) {
      return orgB - orgA;
    }

    // [B329, AI_148] Vigência: em empate (mesmo score ~igual e mesma autoridade),
    // um doc SUBSTITUÍDO/HISTÓRICO perde para o VIGENTE (menor penalidade primeiro).
    // Só desempata — a trava de score acima garante que não rebaixa um chunk
    // claramente mais relevante do doc antigo. Vacatio (8.041) NÃO é penalizado.
    const penA = penalidadeVigencia(a.chunk.metadata.source);
    const penB = penalidadeVigencia(b.chunk.metadata.source);
    if (penA !== penB) {
      return penA - penB;
    }

    const yearA = a.chunk.metadata.year || 0;
    const yearB = b.chunk.metadata.year || 0;
    return yearB - yearA;
  });
}

export function formatarContextoParaPrompt(context: RetrievedContext): string {
  if (context.chunks.length === 0) {
    return "";
  }

  let formatted = `\n\n=== CONTEXTO DE GUIDELINES MÉDICOS ===\n`;
  formatted += `(${context.totalResults} trechos relevantes encontrados)\n\n`;

  for (let i = 0; i < context.chunks.length; i++) {
    const result = context.chunks[i];
    const { chunk, score } = result;

    formatted += `--- Fonte ${i + 1}: ${chunk.metadata.title} ---\n`;
    formatted += `Organização: ${chunk.metadata.organization} | `;
    formatted += `Ano: ${chunk.metadata.year} | `;
    formatted += `Seção: ${chunk.metadata.section || "N/A"}\n`;
    formatted += `Relevância: ${(score * 100).toFixed(1)}%\n\n`;
    formatted += `${chunk.text}\n\n`;
  }

  formatted += `=== FIM DO CONTEXTO DE GUIDELINES ===\n`;

  return formatted;
}

// B229 fase 2 / B328 (AI_148): extrai a lista de DOCUMENTOS (diretrizes) que a
// busca recuperou, a partir dos chunks — para registrar na proveniência da
// análise. Puro/determinístico; NÃO altera o texto do prompt. Deduplica por
// documento (vários chunks vêm da mesma guideline), fica com a MAIOR relevância,
// ordena desc e limita. Omite campos undefined (segurança Firestore).
export function mapearDocumentosRecuperados(
  chunks: SearchResult[],
  limite = 8
): DocumentoRagRecuperado[] {
  const porDoc = new Map<string, DocumentoRagRecuperado>();

  for (const { chunk, score } of chunks) {
    const meta = chunk.metadata;
    const titulo = (meta.title || "").trim();
    if (!titulo) continue;

    const relevancia = Math.round((score || 0) * 100);
    // Dedup pela organização CRUA (um documento tem uma org só); a normalização
    // de "Other" é só para exibição.
    const chave = `${titulo.toLowerCase()}|${(
      meta.organization || ""
    ).toLowerCase()}`;

    const doc: DocumentoRagRecuperado = { titulo, relevancia };
    // "Other" é o balde de "sociedade não identificada" do corpus (prioridade 0
    // no sortByRelevance) → tratar como ausente p/ não parecer uma sociedade.
    if (meta.organization && meta.organization !== "Other") {
      doc.organizacao = meta.organization;
    }
    if (typeof meta.year === "number") doc.ano = meta.year;
    // Sem seção: é do chunk (posição no arquivo), não do documento — ver o tipo.

    const existente = porDoc.get(chave);
    if (!existente || relevancia > existente.relevancia) {
      porDoc.set(chave, doc);
    }
  }

  return Array.from(porDoc.values())
    .sort((a, b) => b.relevancia - a.relevancia)
    .slice(0, limite);
}

// B316 (AI_142): query em PORTUGUÊS que recupera a NORMATIVA BRASILEIRA de
// situação especial (exceção de MELD). B326 (AI_147): extraída p/ constante
// única (era duplicada) e com vaga RESERVADA no retorno (ver fim da função).
export const QUERY_SITUACAO_ESPECIAL =
  "situação especial exceção pontuação MELD transplante hepático ascite refratária síndrome hepatopulmonar colangite de repetição critérios normativa nacional";

export function gerarQueriesAutomaticas(dadosPaciente: {
  tipoAtendimento?: string;
  transplante?: { tempoTransplante?: string; dataTransplante?: string };
  examesLab?: Array<{ valores?: Record<string, unknown> }>;
  consultas?: Array<{ queixaPrincipal?: string; hipoteses?: string }>;
  diagnosticoBase?: string;
  achadosImagem?: Array<{
    trombosePortal?: string;
    ascite?: string;
    lesoesHepaticas?: string;
  }>;
  inscricaoTx?: { diagnosticoBase?: string };
  dadosTransplante?: Array<{ dataTransplante?: string }>;
  avaliacaoClinica?: any;
}): string[] {
  const queries: string[] = [];

  // =============================================
  // 1. QUERIES BASEADAS NOS DIAGNÓSTICOS
  // =============================================
  const textoDiag = [
    dadosPaciente.diagnosticoBase || "",
    dadosPaciente.inscricaoTx?.diagnosticoBase || "",
    ...(dadosPaciente.consultas || []).map((c) => c.hipoteses || ""),
  ]
    .join(" ")
    .toLowerCase();

  // CHC
  if (
    textoDiag.includes("hepatocarcinoma") ||
    textoDiag.includes("chc") ||
    textoDiag.includes("hcc")
  ) {
    queries.push("hepatocellular carcinoma Milan criteria LI-RADS transplant");
  }

  // Hepatite B
  if (textoDiag.includes("hepatite b") || textoDiag.includes("hbv")) {
    queries.push(
      "hepatitis B treatment entecavir tenofovir reactivation prevention"
    );
  }

  // Hepatite C
  if (textoDiag.includes("hepatite c") || textoDiag.includes("hcv")) {
    queries.push("hepatitis C DAA treatment SVR post-transplant recurrence");
  }

  // Hepatite autoimune
  if (textoDiag.includes("hepatite autoimune") || textoDiag.includes("hai")) {
    queries.push(
      "autoimmune hepatitis treatment azathioprine prednisone relapse"
    );
  }

  // Colangite biliar primária
  if (textoDiag.includes("cbp") || textoDiag.includes("colangite biliar")) {
    queries.push("primary biliary cholangitis UDCA obeticholic acid response");
  }

  // Colangite esclerosante
  if (
    textoDiag.includes("cep") ||
    textoDiag.includes("colangite esclerosante")
  ) {
    queries.push(
      "primary sclerosing cholangitis cholangiocarcinoma surveillance transplant"
    );
  }

  // Doença alcoólica
  if (
    textoDiag.includes("alcoólica") ||
    textoDiag.includes("alcoolica") ||
    textoDiag.includes("etílica")
  ) {
    queries.push(
      "alcoholic liver disease hepatitis prednisolone abstinence transplant"
    );
  }

  // Wilson
  if (textoDiag.includes("wilson")) {
    queries.push(
      "Wilson disease trientine zinc penicillamine liver failure transplant"
    );
  }

  // MASLD / NASH
  if (
    textoDiag.includes("nash") ||
    textoDiag.includes("masld") ||
    textoDiag.includes("esteatohepatite")
  ) {
    queries.push("MASLD MASH resmetirom semaglutide fibrosis treatment");
  }

  // =============================================
  // 2. QUERIES BASEADAS NO PERÍODO PÓS-TX
  // =============================================
  const isPosTx = !!(
    dadosPaciente.transplante ||
    (dadosPaciente.dadosTransplante &&
      dadosPaciente.dadosTransplante.length > 0)
  );

  if (isPosTx && dadosPaciente.transplante?.tempoTransplante) {
    const tempo = dadosPaciente.transplante.tempoTransplante;

    // Extrair se é precoce, intermediário ou tardio
    const matchAnos = tempo.match(/(\d+)a/);
    const matchMeses = tempo.match(/(\d+)m/);
    const anos = matchAnos ? parseInt(matchAnos[1]) : 0;
    const meses = matchMeses ? parseInt(matchMeses[1]) : 0;
    const totalMeses = anos * 12 + meses;

    if (totalMeses <= 1) {
      // 0-30 dias
      queries.push(
        "early post-transplant hepatic artery thrombosis primary graft dysfunction"
      );
      queries.push(
        "acute cellular rejection Banff criteria methylprednisolone"
      );
    } else if (totalMeses <= 6) {
      // 1-6 meses
      queries.push("acute rejection CMV infection post liver transplant");
      queries.push("biliary anastomotic stricture ischemic cholangiopathy");
    } else if (totalMeses <= 12) {
      // 6-12 meses
      queries.push(
        "chronic ductopenic rejection recurrent disease post transplant"
      );
      queries.push(
        "calcineurin inhibitor nephrotoxicity everolimus conversion"
      );
    } else {
      // > 1 ano
      queries.push(
        "long-term post-transplant PTLD cardiovascular disease de novo malignancy"
      );
      queries.push("immunosuppression minimization tacrolimus withdrawal");
    }
  }

  // =============================================
  // 3. QUERIES BASEADAS NOS ACHADOS DE IMAGEM
  // =============================================
  const achados = dadosPaciente.achadosImagem || [];

  // Trombose portal
  if (
    achados.some(
      (a) =>
        a.trombosePortal &&
        a.trombosePortal !== "NÃO" &&
        a.trombosePortal !== ""
    )
  ) {
    queries.push(
      "portal vein thrombosis Yerdel classification anticoagulation transplant"
    );
  }

  // Ascite
  if (achados.some((a) => a.ascite === "SIM")) {
    queries.push(
      "refractory ascites TIPS SBP prophylaxis hepatorenal syndrome"
    );
  }

  // Lesões hepáticas
  if (achados.some((a) => a.lesoesHepaticas && a.lesoesHepaticas.length > 0)) {
    queries.push(
      "liver lesion characterization LI-RADS HCC cholangiocarcinoma differential"
    );
  }

  // =============================================
  // 4. QUERIES BASEADAS NOS EXAMES LABORATORIAIS
  // =============================================
  // B358 (AI_156): a lista chega em ordem ASCENDENTE (anonimizarDados ordena do
  // mais antigo ao mais recente) — o "último exame" é o FINAL da lista. Antes
  // lia-se [0], o exame mais ANTIGO do paciente.
  const examesLabLista = dadosPaciente.examesLab || [];
  const ultimoExame = examesLabLista[examesLabLista.length - 1]?.valores;
  if (ultimoExame) {
    const ast = Number(ultimoExame.AST) || 0;
    const alt = Number(ultimoExame.ALT) || 0;

    if (ast > 100 || alt > 100) {
      queries.push(
        "elevated transaminases differential diagnosis hepatocellular injury"
      );
      if (isPosTx) {
        queries.push(
          "graft dysfunction rejection hepatic artery thrombosis workup"
        );
      }
    }

    const ggt = Number(ultimoExame.GGT) || 0;
    const fa = Number(ultimoExame.FA) || 0;
    if (ggt > 100 || fa > 200) {
      queries.push(
        "cholestatic pattern biliary stricture drug-induced liver injury"
      );
    }

    // Hiponatremia
    const sodio = Number(ultimoExame.Sodio) || 0;
    if (sodio > 0 && sodio < 130) {
      queries.push("hyponatremia cirrhosis MELD-Na dilutional hepatorenal");
    }

    // B358 (AI_156): disfunção renal no pré-Tx → consulta própria. Sem ela o
    // consenso ADQI/ICA 2024 (AKI/AKD/HRS-CKD na cirrose) nunca era recuperado
    // num cirrótico com creatinina persistentemente alta.
    const creatinina = Number(ultimoExame.Creatinina) || 0;
    if (
      !isPosTx &&
      creatinina >= 1.5 &&
      !queries.some((q) => q.includes("acute kidney injury") || q.includes("AKI"))
    ) {
      queries.push(
        "acute kidney injury cirrhosis hepatorenal syndrome HRS-AKI HRS-CKD albumin terlipressin"
      );
    }
  }

  // =============================================
  // 5. FALLBACK: Se não gerou nenhuma query
  // =============================================
  if (queries.length === 0) {
    queries.push(
      "chronic liver disease cirrhosis management guidelines recommendations"
    );
  }

  // =============================================
  // 6. ✅ NOVO: QUERIES BASEADAS NA AVALIAÇÃO CLÍNICA
  // =============================================
  const av = (dadosPaciente as any).avaliacaoClinica;
  if (av) {
    if (
      av.ascite === true &&
      !queries.some((q) => q.includes("ascite") || q.includes("refractory"))
    ) {
      queries.push(
        "ascites management diuretics paracentesis refractory TIPS SBP"
      );
    }
    if (
      av.encefalopatia === true &&
      !queries.some((q) => q.includes("encephalopathy"))
    ) {
      queries.push(
        "hepatic encephalopathy lactulose rifaximin West Haven grading"
      );
    }
    if (
      (av.varizesEsofago === true ||
        av.varizesGastrica === true ||
        av.hdaPrevia === true) &&
      !queries.some((q) => q.includes("varic"))
    ) {
      queries.push(
        "esophageal varices prophylaxis carvedilol band ligation Baveno VII CSPH"
      );
    }
    if (av.shr === true && !queries.some((q) => q.includes("hepatorenal"))) {
      queries.push(
        "hepatorenal syndrome terlipressin albumin AKI ICA criteria"
      );
    }
    if (
      av.trombosePorta === true &&
      !queries.some((q) => q.includes("portal vein thrombosis"))
    ) {
      queries.push(
        "portal vein thrombosis Yerdel classification anticoagulation transplant"
      );
    }
    if (
      av.sarcopenia === true &&
      !queries.some((q) => q.includes("sarcopenia") || q.includes("frailty"))
    ) {
      queries.push(
        "sarcopenia liver frailty index nutrition cirrhosis transplant"
      );
    }
    if (av.shdHepatopulmonar === true) {
      queries.push(
        "hepatopulmonary syndrome PaO2 shunt liver transplant indication"
      );
    }
  }

  // =============================================
  // 7. LISTAGEM / SITUAÇÃO ESPECIAL PARA TRANSPLANTE (pré-Tx)
  // =============================================
  if (!isPosTx) {
    const meld =
      Number((ultimoExame as any)?.MELD) ||
      Number((ultimoExame as any)?.meld) ||
      0;
    if (meld >= 15) {
      queries.push("liver transplant listing criteria MELD allocation organ");
    }
    // A portaria do SNT é um documento em PT e não casava com as queries em
    // inglês → a IA ficava sem âncora normativa e não conseguia apontar critérios
    // de SE que o paciente preenche (ascite refratária, SHP, etc.). Roda em TODO
    // pré-Tx (a análise deve checar SE).
    queries.push(QUERY_SITUACAO_ESPECIAL);
  }

  const LIMITE = 7;
  const dedup = Array.from(new Set(queries));

  // B326 (AI_147): a query PT de situação especial é a ÚLTIMA a ser montada e,
  // num pré-Tx CARREGADO (muitas etiologias/achados), seria cortada pelo slice —
  // justamente nos pacientes mais graves, os mais prováveis de qualificar p/ SE.
  // Reservamos 1 vaga p/ ela dentro do limite (custo igual: seguem 7 queries no
  // total), sem depender da ordem de montagem.
  if (!isPosTx) {
    const outras = dedup.filter((q) => q !== QUERY_SITUACAO_ESPECIAL);
    return [...outras.slice(0, LIMITE - 1), QUERY_SITUACAO_ESPECIAL];
  }

  // Remover duplicatas e limitar (B316: 7 queries).
  return dedup.slice(0, LIMITE);
}

export async function buscarContextoMultiplo(
  queries: string[],
  options?: {
    categories?: GuidelineCategory[];
    // B351 passo 5b (AI_153) — filtro por ORGANIZAÇÃO. O caminho já existia até o
    // Pinecone (ragClient → api/rag-search: montarFiltroPinecone monta o $and),
    // mas parava aqui. É o que permite ao doador receber o consenso de doador
    // vivo em vez do corpus de cirrose.
    organizations?: string[];
    maxChunksTotal?: number;
    // B358 (AI_156): false = rerank ÚNICO (3 primeiras queries juntas) + teto
    // macio, como antes. Usado pelo doador vivo, cujo caminho foi calibrado no
    // índice real (B351) e tem um só documento útil — o rodízio por tema puxava
    // trecho de receptor para as vagas.
    distribuirPorTema?: boolean;
  }
): Promise<RetrievedContext> {
  const startTime = Date.now();

  // AI_16: Se reranker ativo, puxar mais candidatos do Pinecone (topK=12)
  const useReranker = ragConfig.reranker?.enabled === true;
  const pineconeTopK = useReranker ? ragConfig.reranker!.topK : 5;

  // ═══════════════════════════════════════════════════════
  // AI_19: HYBRID RETRIEVAL — Dense + Sparse (BM25)
  // B357 (AI_156): as DUAS buscas rodam no servidor numa chamada só; o BM25
  // devolve os trechos já com texto (antes, no navegador, os achados só-BM25
  // eram descartados). RRF + boost + rerank seguem aqui, inalterados.
  // B95 4.3.B.2 (AI_65): dense consolidado em 1 round-trip ao backend
  // (era 4 round-trips paralelos via buscarContextoGuidelines)
  // ═══════════════════════════════════════════════════════

  // 1. Enriquecer queries (preserva enrichQuery que era aplicado em buscarContextoGuidelines)
  // B316 (AI_142): 4→7. O corte em 4 descartava queries úteis (gerarQueriesAutomaticas
  // produz até 7), incluindo a query PT de SITUAÇÃO ESPECIAL — que ficava órfã e nunca
  // chegava ao Pinecone. Em casos simples (≤4 queries) nada muda; nos complexos, melhora
  // o recall. O contexto final segue limitado por maxChunksTotal + minScore + reranker.
  const enrichedQueries = queries.slice(0, 7).map((q) => enrichQuery(q));

  // 2+3. Busca densa + BM25 CONSOLIDADAS — 1 round-trip ao backend
  //    Servidor faz: embed (paralelo) → Pinecone (paralelo) → dedup, e o BM25
  //    sobre as queries ORIGINAIS (sem o enriquecimento em inglês), juntadas
  //    como antes (B316: 3→7 — a query PT de situação especial casa muito bem
  //    no BM25 lexical contra a portaria em PT).
  //    B127 (AI_67): repassa options.categories para o backend — antes era
  //    silenciosamente descartado (parâmetro existia na assinatura mas não fluía).
  //    Se a busca por palavras falhar no servidor, `bm25` vem vazio e segue só denso.
  const { densos: denseResultsRaw, bm25: sparseResults } =
    await buscarChunksHibridos(enrichedQueries, queries.slice(0, 7), {
      categories: options?.categories,
      organizations: options?.organizations,
      topK: pineconeTopK,
    });

  // 5. Filtrar minScore (preserva 0.55 que era aplicado em buscarContextoGuidelines)
  const minScore = 0.55;
  const denseFiltered = denseResultsRaw.filter((r) => r.score >= minScore);

  // 6. Aplicar priorização por organização ANTES do RRF.
  //    Necessário porque o backend devolve chunks por score absoluto descendente,
  //    sem o boost ILTS=10 / AASLD=9 / EASL=9 / AST=8 / IDSA=7 que era aplicado
  //    a cada chamada individual de buscarContextoGuidelines.
  //    Sem isto, o RRF usaria apenas score absoluto e perderíamos sinal clínico.
  const allDenseResults = sortByRelevance(denseFiltered);

  console.log(
    `🌐 Dense consolidado: ${denseResultsRaw.length} brutos → ${allDenseResults.length} após filter+sort`
  );

  const maxChunks = options?.maxChunksTotal || 6;
  let finalResults: SearchResult[];

  if (useReranker) {
    // 7. RRF: Fundir rankings dense + sparse
    const fusedResults = reciprocalRankFusion(allDenseResults, sparseResults);
    console.log(
      `🔀 RRF: ${allDenseResults.length} dense + ${sparseResults.length} sparse → ${fusedResults.length} fundidos`
    );

    // AI_20: Preencher texto dos resultados BM25 com dados do Pinecone
    const denseTextMap = new Map<string, string>();
    for (const result of allDenseResults) {
      if (result.chunk.text) {
        denseTextMap.set(result.chunk.id, result.chunk.text);
      }
    }
    for (const result of fusedResults) {
      if (!result.chunk.text && denseTextMap.has(result.chunk.id)) {
        result.chunk.text = denseTextMap.get(result.chunk.id)!;
      }
    }
    // Filtrar resultados sem texto (rede de segurança — desde o B357 o servidor
    // já devolve os achados do BM25 com texto). Teto de 100 = MAX_CHUNKS_INPUT
    // do /api/rag-rerank (7 queries × 12 densos + 12 BM25 cabe, mas não estoura).
    const resultsWithText = fusedResults
      .filter((r) => r.chunk.text)
      .slice(0, 100);
    const droppedCount = fusedResults.length - resultsWithText.length;
    if (droppedCount > 0) {
      console.log(
        `⚠️ ${droppedCount} resultado(s) BM25-only sem texto descartado(s)`
      );
    }

    // 8. Reranker cross-encoder via backend (B95 4.3.B.2: era rerankChunks local)
    // B358 (AI_156): rerank POR TEMA. Antes era UMA chamada com as 3 primeiras
    // queries juntas — nos casos com 4+ temas, um tema inteiro (ex.: a etiologia
    // alcoólica, ou a norma de situação especial, que vai por último) perdia
    // para os demais e sumia do contexto. Agora cada query é reranqueada sozinha
    // (em paralelo) e as vagas são distribuídas em rodízio entre os temas, com
    // teto macio de 2 trechos por documento.
    if (resultsWithText.length > maxChunks && options?.distribuirPorTema === false) {
      const reranqueados = await rerankChunksRemoto(
        queries.slice(0, 3).join(". "),
        resultsWithText,
        Math.min(resultsWithText.length, maxChunks * 3)
      );
      finalResults = limitarPorDocumento(reranqueados, maxChunks);
    } else if (resultsWithText.length > maxChunks) {
      const temas = queries.slice(0, 7);
      const topKTema = Math.min(resultsWithText.length, maxChunks * 3);
      const listas = await Promise.all(
        temas.map((q) =>
          rerankChunksRemoto(q, resultsWithText, topKTema).catch((err) => {
            console.warn("⚠️ Rerank de um tema falhou, segue sem ele:", err);
            return [] as SearchResult[];
          })
        )
      );
      finalResults = selecionarPorTema(listas, maxChunks);
      if (finalResults.length === 0) {
        finalResults = limitarPorDocumento(resultsWithText, maxChunks);
      }
    } else {
      finalResults = resultsWithText.slice(0, maxChunks);
    }
  } else {
    // Fallback sem reranker: RRF + corte
    // (seenIds reconstruído localmente — não vem mais do loop de dedup que foi removido)
    const seenIds = new Set(allDenseResults.map((r) => r.chunk.id));
    const uniqueSparseResults = sparseResults.filter(
      (r) => !seenIds.has(r.chunk.id)
    );
    const fusedResults = reciprocalRankFusion(
      allDenseResults,
      uniqueSparseResults
    );
    finalResults = limitarPorDocumento(fusedResults, maxChunks);
  }

  return {
    chunks: finalResults,
    query: queries.join(" | "),
    totalResults: finalResults.length,
    searchTimeMs: Date.now() - startTime,
  };
}

/**
 * 🔄 RE-RETRIEVAL: Extrair tópicos clínicos da análise da Etapa 1
 * para busca complementar na Etapa 2
 */
export function extrairTopicosParaReRetrieval(analiseTexto: string): string[] {
  const queries: string[] = [];
  const texto = analiseTexto.toLowerCase();

  // Protocolos e scores mencionados na análise
  const protocolos: Record<string, string> = {
    olthoff:
      "Olthoff criteria early allograft dysfunction EAD liver transplant",
    banff: "Banff criteria acute cellular rejection liver transplant grading",
    milan: "Milan criteria hepatocellular carcinoma transplant eligibility",
    ucsf: "UCSF criteria extended HCC transplant selection",
    baveno: "Baveno VII portal hypertension varices CSPH beta-blocker",
    "li-rads": "LI-RADS hepatocellular carcinoma CT MRI diagnostic criteria",
    "lr-m": "LI-RADS LR-M non-HCC malignancy biopsy recommendation",
    "lr-tr": "LI-RADS LR-TR treatment response viable assessment",
    cast: "CAST cholangiopathy ischemic hepatic artery thrombosis retransplant",
    iri: "ischemia reperfusion injury liver transplant graft function",
    ead: "early allograft dysfunction primary nonfunction liver transplant",
    tah: "hepatic artery thrombosis diagnosis doppler management retransplant",
    pbe: "spontaneous bacterial peritonitis prophylaxis norfloxacin albumin",
    meld: "MELD score allocation liver transplant exception points",
    "child-pugh": "Child-Pugh score cirrhosis prognosis classification",
    sarcopenia: "sarcopenia cirrhosis frailty liver transplant assessment",
    csph: "clinically significant portal hypertension HVPG elastography varices",
    colangiopatia:
      "ischemic cholangiopathy biliary stricture post-transplant MRCP",
    "rejeição crônica":
      "chronic rejection vanishing bile duct syndrome ductopenic",
    "recidiva viral":
      "hepatitis C recurrence post-transplant DAA treatment SVR",
    cmv: "CMV infection post-transplant valganciclovir prophylaxis treatment",
    ebv: "EBV infection PTLD post-transplant lymphoproliferative disorder",
    "down-staging": "downstaging HCC TACE Y90 bridging transplant criteria",
    tpmt: "TPMT thiopurine azathioprine toxicity testing autoimmune hepatitis",
    // --- AI_23: Síndromes hepatológicas ---
    hepatopulmonar:
      "hepatopulmonary syndrome shunt hypoxemia liver transplant screening",
    hepatorrenal:
      "hepatorenal syndrome type 1 type 2 terlipressin albumin transplant",
    "rejeição humoral":
      "antibody-mediated rejection DSA C4d liver transplant treatment",
    "rejeição celular":
      "acute cellular rejection Banff RAI liver transplant steroids",
    "sobreposição hai":
      "overlap syndrome PBC AIH autoimmune cholangiopathy UDCA immunosuppression",
    "hipertensão portal não":
      "non-cirrhotic portal hypertension NCPH idiopathic obliterative",
    nefrotoxicidade:
      "calcineurin inhibitor nephrotoxicity tacrolimus CKD renal sparing everolimus",
    "diabetes pós":
      "post-transplant diabetes mellitus NODAT tacrolimus management",
    dislipidemia:
      "dyslipidemia post-transplant statin everolimus metabolic syndrome",
    // --- AI_23: Cirurgia hepatobiliopancreática ---
    whipple: "pancreaticoduodenectomy Whipple procedure pancreatic head tumor",
    pancreatectomia:
      "distal pancreatectomy splenectomy pancreatic body tail tumor",
    hepatectomia:
      "liver resection hepatectomy anatomical segmentectomy HCC colorectal metastases",
    "derivação biliar":
      "biliary reconstruction Roux-en-Y hepaticojejunostomy bile duct injury",
    "estenose biliar":
      "biliary stricture anastomotic post-transplant ERCP stent dilation",
    colangiocarcinoma:
      "cholangiocarcinoma hilar Klatskin perihilar intrahepatic transplant resection",
    "cisto hepático":
      "hepatic cyst Caroli disease polycystic liver ADPKD fenestration",
    "abscesso hepático":
      "hepatic abscess pyogenic amoebic drainage antibiotics percutaneous",
    pancreatite:
      "acute chronic pancreatitis necrotizing step-up approach drainage",
    "fístula biliar":
      "bile leak fistula post-cholecystectomy post-hepatectomy ERCP drain",
    "trauma hepático":
      "liver trauma blunt penetrating WSES AAST grade management",
  };

  for (const [termo, queryRelacionada] of Object.entries(protocolos)) {
    if (texto.includes(termo)) {
      queries.push(queryRelacionada);
    }
  }

  // Limitar a 4 queries para não estourar tempo
  return queries.slice(0, 4);
}
